import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const WhyBatterySection = () => {
  const benefits = [
    {
      icon: 'Sun',
      title: 'Maximale zonne-energie benutting',
      description: 'Gebruik uw zelf opgewekte stroom ook \'s avonds en \'s nachts',
      stat: '70% meer eigen verbruik'
    },
    {
      icon: 'TrendingDown',
      title: 'Profiteer van dynamische tarieven',
      description: 'Laad op tijdens lage prijzen, gebruik tijdens piekuren',
      stat: 'Tot €600 extra besparing'
    },
    {
      icon: 'Shield',
      title: 'Back-up bij stroomuitval',
      description: 'Blijf voorzien van stroom tijdens netuitval',
      stat: '24/7 zekerheid'
    },
    {
      icon: 'Zap',
      title: 'Ondersteun het energienet',
      description: 'Verdien bij door netbalancering en grid services',
      stat: 'Extra inkomsten mogelijk'
    }
  ];

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Waarom een thuisbatterij steeds belangrijker wordt
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            De energiemarkt verandert snel. Met een thuisbatterij bent u klaar voor de toekomst.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Benefits Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {benefits?.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-white rounded-xl p-6 shadow-elevation-1 hover:shadow-elevation-2 transition-all"
              >
                <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name={benefit?.icon} size={24} className="text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">{benefit?.title}</h3>
                <p className="text-sm text-muted-foreground mb-3">{benefit?.description}</p>
                <p className="text-sm font-semibold text-green-600">{benefit?.stat}</p>
              </motion.div>
            ))}
          </div>

          {/* Visual Comparison */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8"
          >
            <h3 className="text-xl font-semibold text-foreground mb-6">Nederlandse energiemarkt</h3>
            
            <div className="space-y-6">
              <div className="bg-white rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">Dynamische tarieven</span>
                  <Icon name="TrendingUp" size={20} className="text-primary" />
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Prijsverschil tussen dal- en piektarief kan oplopen tot €0,40/kWh
                </p>
                <div className="flex items-center space-x-2">
                  <div className="flex-1 bg-green-100 h-2 rounded-full"></div>
                  <span className="text-xs font-semibold text-green-600">Optimaal benutten</span>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">Salderingsregeling</span>
                  <Icon name="AlertCircle" size={20} className="text-warning" />
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Wordt afgebouwd tot 2031 - batterij wordt steeds aantrekkelijker
                </p>
                <div className="flex items-center space-x-2">
                  <div className="flex-1 bg-warning/20 h-2 rounded-full"></div>
                  <span className="text-xs font-semibold text-warning">Voorbereiden</span>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">Grid balancing</span>
                  <Icon name="Zap" size={20} className="text-success" />
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Verdien mee aan netbalancering en frequentieregeling
                </p>
                <div className="flex items-center space-x-2">
                  <div className="flex-1 bg-success/20 h-2 rounded-full"></div>
                  <span className="text-xs font-semibold text-success">Extra inkomsten</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Image Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          <div className="relative rounded-xl overflow-hidden shadow-elevation-2">
            <Image
              src="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=600&h=400&fit=crop"
              alt="Zonnepanelen op dak met batterijopslag systeem voor maximale energie-efficiëntie"
              className="w-full h-64 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
              <p className="text-white font-medium">Optimale combinatie met zonnepanelen</p>
            </div>
          </div>
          <div className="relative rounded-xl overflow-hidden shadow-elevation-2">
            <Image
              src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop"
              alt="Smart meter en energiemonitoring systeem voor dynamische tarieven optimalisatie"
              className="w-full h-64 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
              <p className="text-white font-medium">Slimme integratie met dynamische tarieven</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyBatterySection;